import type { PropsWithChildren } from 'react';
import React, { useState } from 'react';
import styled from 'styled-components';

import { HorizontalLayout } from './Layout';
import { Portal } from './Portal';
import { Text, Title } from './Text';

export const Modal = ({
    onClose,
    modalTitle,
    children,
    $maxWidth,
    allowOverflow = false,
}: PropsWithChildren<{ onClose: () => void; modalTitle?: string; allowOverflow?: boolean; $maxWidth?: string }>) => {
    const [isSelf, setIsSelf] = useState(false);

    return (
        <Portal id="modal">
            <Curtain
                onMouseDown={(event: React.SyntheticEvent<HTMLDivElement>) => {
                    event.stopPropagation();
                    if (event.target === event.currentTarget) {
                        setIsSelf(true);
                    }
                }}
                onMouseUp={(event: React.SyntheticEvent<HTMLDivElement>) => {
                    event.stopPropagation();
                    if (event.target === event.currentTarget && isSelf) {
                        onClose();
                    }
                    setIsSelf(false);
                }}
            >
                <Container
                    $allowOverflow={allowOverflow}
                    onClick={(event: React.SyntheticEvent) => {
                        event.stopPropagation();
                    }}
                    $maxWidth={$maxWidth}
                >
                    {modalTitle ? (
                        <ModalTitle>
                            <Title modal>{modalTitle}</Title>
                        </ModalTitle>
                    ) : null}
                    {children}
                </Container>
            </Curtain>
        </Portal>
    );
};

export const Curtain = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: ${({ theme }) => theme.container.backdrop};
    display: flex;
    align-items: center;
    justify-content: center;
    /* the modal (see Container) and the shadow backdrop must be above all other z-index-ed components on the page */
    z-index: 10;

    /* this will make modals filling the container have some space around them */
    padding: 12px;
`;

const Container = styled.div<{ $allowOverflow: boolean; $maxWidth?: string }>`
    position: relative;
    background-color: ${({ theme }) => theme.container.primaryBackground};
    border-radius: 4px;
    z-index: 11;

    /* scroll if content is larger than visible area */
    max-height: 90vh;
    overflow: ${({ $allowOverflow }) => ($allowOverflow ? 'visible' : 'auto')};
    width: 60%;
    max-width: ${({ $maxWidth }) => $maxWidth ?? '700px'};
    display: flex;
    flex-direction: column;
`;

const ModalTitle = styled.div`
    width: 100%;
    text-align: center;
    padding: 16px 0;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};
`;

export const ModalContent = styled.div<{ $centered?: boolean }>`
    max-width: ${({ $centered }) => $centered && '80%'};
    padding: 32px;
    min-height: 200px;
`;

export const ButtonsContainer = styled(HorizontalLayout)`
    width: 100%;
    padding: 16px 0;
    justify-content: center;
    border-top: 1px solid ${({ theme }) => theme.border.light};
`;

export const ModalText = ({ children }: PropsWithChildren) => (
    <Text $themeColor="subtle" $centered>
        {children}
    </Text>
);
